'use client';

/**
 * Universal share button — used on prompt / profile / collection pages.
 *
 * Click → dropdown with Copy link / X / Facebook / (native share if available).
 * All the targets open in a new tab via pre-filled intent URLs so the user
 * arrives on the third-party site with the content already staged.
 *
 * Accessibility: dismisses on Escape, click-outside, and after a successful
 * copy (so the dropdown doesn't linger).
 *
 * The pre-filled text is the `title` prop — kept short. Longer descriptions
 * go to the platform via the Open Graph tags we already ship (see
 * `src/lib/seo.ts` + sibling `layout.tsx` server components). Those render
 * in the embedded preview card on Facebook / X once the link is posted.
 */

import { useEffect, useRef, useState } from 'react';
import { useToast } from '@/lib/toast';
import { copyToClipboard } from '@/lib/clipboard';

type Props = {
  /** Absolute URL or path to share. If a path, we prefix with `window.location.origin`. */
  url: string;
  /** Short text to pre-fill in Twitter/X (falls back to the page title via OG). */
  title: string;
  /** Optional: className override for the button. */
  className?: string;
  /** Optional: label next to the icon. Defaults to "Share". */
  label?: string;
  /** Optional: render compact icon-only variant. */
  iconOnly?: boolean;
};

export default function ShareButton({ url, title, className, label = 'Share', iconOnly = false }: Props) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [canNativeShare, setCanNativeShare] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Feature-detect. Safari iOS / Android Chrome have `navigator.share`; desktop
    // Chrome gained it more recently but it's still inconsistent — check at runtime.
    setCanNativeShare(typeof navigator !== 'undefined' && typeof navigator.share === 'function');
  }, []);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const absoluteUrl = (() => {
    if (/^https?:\/\//i.test(url)) return url;
    if (typeof window === 'undefined') return url;
    const origin = window.location.origin.replace(/\/$/, '');
    return origin + (url.startsWith('/') ? url : `/${url}`);
  })();

  const onCopy = async () => {
    const ok = await copyToClipboard(absoluteUrl);
    if (ok) {
      toast.success('Link copied');
      setOpen(false);
    } else {
      toast.error('Couldn\u2019t copy link');
    }
  };

  const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(absoluteUrl)}`;
  const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(absoluteUrl)}`;

  const tryNativeShare = async () => {
    try {
      await navigator.share({ title, url: absoluteUrl });
      setOpen(false);
    } catch {
      // User canceled — no toast needed.
    }
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={label}
        title={label}
        className={className || (iconOnly ? 'ps-btn ps-btn-icon ps-btn-ghost' : 'ps-btn ps-btn-secondary ps-btn-sm')}
      >
        <span className="material-symbols-outlined ps-icon">ios_share</span>
        {!iconOnly && <span>{label}</span>}
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            minWidth: 200,
            padding: 6,
            background: 'var(--bg-raised)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 16px 40px -12px rgba(0,0,0,0.35), 0 4px 10px -4px rgba(0,0,0,0.12)',
            zIndex: 50,
          }}
        >
          <ShareItem
            icon="link"
            label="Copy link"
            onClick={onCopy}
          />
          <ShareItem
            icon="close"
            iconChar={<XIcon />}
            label="Share on X"
            onClick={() => { window.open(tweetUrl, '_blank', 'noopener,noreferrer'); setOpen(false); }}
          />
          <ShareItem
            icon="facebook"
            iconChar={<FacebookIcon />}
            label="Share on Facebook"
            onClick={() => { window.open(fbUrl, '_blank', 'noopener,noreferrer'); setOpen(false); }}
          />
          {canNativeShare && (
            <ShareItem
              icon="ios_share"
              label="More…"
              onClick={tryNativeShare}
            />
          )}
        </div>
      )}
    </div>
  );
}

function ShareItem({
  icon,
  iconChar,
  label,
  onClick,
}: {
  icon: string;
  iconChar?: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      role="menuitem"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        padding: '8px 12px',
        background: 'transparent',
        border: 'none',
        borderRadius: 'var(--radius-sm)',
        cursor: 'pointer',
        color: 'var(--ink)',
        fontSize: 13,
        fontFamily: 'var(--font-sans)',
        textAlign: 'left',
        transition: 'background 0.08s',
      }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-soft)'; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
    >
      <span style={{ width: 18, display: 'inline-flex', justifyContent: 'center', color: 'var(--ink-3)' }}>
        {iconChar ?? <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{icon}</span>}
      </span>
      {label}
    </button>
  );
}

// Inline SVGs for brand icons — Material Symbols doesn't have an X / Facebook glyph.
function XIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25h6.827l4.713 6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z"/>
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  );
}
